import { toCdnUrl } from '../utils/cdn';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Tarif gorsellerini CDN URL'ine cevirir (tek tarif, dizi veya paginated liste)
const withCdnImages = (payload) => {
  if (!payload) return payload;

  if (Array.isArray(payload)) return payload.map(withCdnImages);

  // Paginated response: { data: [...], current_page, total, ... }
  if (Array.isArray(payload.data)) {
    return { ...payload, data: payload.data.map(withCdnImages) };
  }

  const recipe = { ...payload };

  if (recipe.image) {
    recipe.image = toCdnUrl(recipe.image);
  }

  if (Array.isArray(recipe.screenshots)) {
    recipe.screenshots = recipe.screenshots.map((shot) => ({
      ...shot,
      image_url: toCdnUrl(shot.image_url),
    }));
  }

  return recipe;
};

class RecipeAPI {
  // Tüm tarifleri getir
  async getRecipes(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.category) queryParams.append('category', params.category);
      if (params.difficulty) queryParams.append('difficulty', params.difficulty);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);

      const url = `${API_BASE_URL}/recipes${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Tarifler yüklenemedi');
      }

      const data = await response.json();
      return withCdnImages(data.data);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Tek tarif detayı getir
  async getRecipe(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/recipes/${id}`);

      if (!response.ok) {
        throw new Error('Tarif bulunamadı');
      }

      const data = await response.json();
      return withCdnImages(data.data);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Yeni tarif oluştur
  async createRecipe(recipeData) {
    try {
      const formData = new FormData();

      // Text fields
      formData.append('title', recipeData.title);
      formData.append('category', recipeData.category);
      formData.append('prep_time', recipeData.prep_time);
      formData.append('servings', recipeData.servings);
      formData.append('difficulty', recipeData.difficulty);
      formData.append('description', recipeData.description);

      // Arrays
      recipeData.ingredients.forEach((ingredient, index) => {
        formData.append(`ingredients[${index}]`, ingredient);
      });

      recipeData.instructions.forEach((instruction, index) => {
        formData.append(`instructions[${index}]`, instruction);
      });

      // Files
      if (recipeData.image) {
        formData.append('image', recipeData.image);
      }

      if (recipeData.screenshots && recipeData.screenshots.length > 0) {
        recipeData.screenshots.forEach((screenshot) => {
          formData.append('screenshots[]', screenshot);
        });
      }

      const response = await fetch(`${API_BASE_URL}/recipes`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Backend'den gelen detaylı hata mesajlarını işle
        if (errorData.errors) {
          // Validation hataları varsa
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          throw new Error(errorMessages);
        }
        throw new Error(errorData.message || 'Tarif oluşturulamadı');
      }

      const data = await response.json();
      return withCdnImages(data.data);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Tarif güncelle
  async updateRecipe(id, recipeData) {
    try {
      const formData = new FormData();
      formData.append('_method', 'PUT');

      // Only append fields that are provided
      if (recipeData.title) formData.append('title', recipeData.title);
      if (recipeData.category) formData.append('category', recipeData.category);
      if (recipeData.prep_time) formData.append('prep_time', recipeData.prep_time);
      if (recipeData.servings) formData.append('servings', recipeData.servings);
      if (recipeData.difficulty) formData.append('difficulty', recipeData.difficulty);
      if (recipeData.description) formData.append('description', recipeData.description);

      if (recipeData.ingredients) {
        recipeData.ingredients.forEach((ingredient, index) => {
          formData.append(`ingredients[${index}]`, ingredient);
        });
      }

      if (recipeData.instructions) {
        recipeData.instructions.forEach((instruction, index) => {
          formData.append(`instructions[${index}]`, instruction);
        });
      }

      if (recipeData.image) {
        formData.append('image', recipeData.image);
      }

      if (recipeData.screenshots && recipeData.screenshots.length > 0) {
        recipeData.screenshots.forEach((screenshot) => {
          formData.append('screenshots[]', screenshot);
        });
      }

      // Silinecek screenshot ID'leri
      if (recipeData.remove_screenshot_ids && recipeData.remove_screenshot_ids.length > 0) {
        recipeData.remove_screenshot_ids.forEach((id) => {
          formData.append('remove_screenshot_ids[]', id);
        });
      }

      const response = await fetch(`${API_BASE_URL}/recipes/${id}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Backend'den gelen detaylı hata mesajlarını işle
        if (errorData.errors) {
          // Validation hataları varsa
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          throw new Error(errorMessages);
        }
        throw new Error(errorData.message || 'Tarif güncellenemedi');
      }

      const data = await response.json();
      return withCdnImages(data.data);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Tarif sil
  async deleteRecipe(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/recipes/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Tarif silinmedi');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }
}

const recipeAPIInstance = new RecipeAPI();
export default recipeAPIInstance;
